import React, { useState, useEffect } from 'react';
import RichTextEditor from './RichTextEditor';
import api from '../services/api';

interface AddCategoryModalProps {
  onClose: () => void;
  onSave: () => void;
  workspaceId: string;
  category?: {
    _id: string;
    name: string;
    description?: string;
    projectId: string;
    projectName?: string;
    order: number;
    isActive: boolean;
  };
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  onClose,
  onSave,
  workspaceId,
  category
}) => {
  const [projects, setProjects] = React.useState<Array<{ _id: string; name: string }>>([]);
  const [formData, setFormData] = useState({
    order: 1,
    projectId: '',
    name: '',
    description: '',
    isActive: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get(`/workspaces/${workspaceId}/projects`, {
          params: { limit: 1000, sortBy: 'name', sortOrder: 'asc' }
        });
        setProjects(response.data.projects);
      } catch (err) {
        console.error('Error fetching projects:', err);
      }
    };

    fetchProjects();
  }, [workspaceId]);

  useEffect(() => {
    if (category) {
      setFormData({
        order: category.order,
        projectId: category.projectId,
        name: category.name,
        description: category.description || '',
        isActive: category.isActive
      });
    } else {
      // Calculate next order number
      const nextOrder = 1; // You can calculate this based on existing categories
      setFormData({
        order: nextOrder,
        projectId: '',
        name: '',
        description: '',
        isActive: true
      });
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.projectId) {
      setError('Project is required');
      return;
    }

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setSaving(true);

      if (category) {
        // Update existing category
        await api.put(
          `/workspaces/${workspaceId}/pkg-categories/${category._id}`,
          formData,
        );
      } else {
        // Create new category
        await api.post(
          `/workspaces/${workspaceId}/pkg-categories`,
          formData,
        );
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save category');
      console.error('Error saving category:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h3>{category ? 'Edit Category' : 'Add Category'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '6px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label htmlFor="order" className="form-label">
                  Order <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  id="order"
                  className="form-input"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  required
                  disabled={saving}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="projectId" className="form-label">
                  Project <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  id="projectId"
                  className="form-input"
                  value={formData.projectId}
                  onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                  required
                  disabled={saving || !!category}
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label htmlFor="name" className="form-label">
                Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={saving}
                placeholder="Enter category name"
              />
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <RichTextEditor
                content={formData.description}
                onChange={(content) => setFormData({ ...formData, description: content })}
                placeholder="Enter description"
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  disabled={saving}
                  style={{ width: 'auto', margin: 0 }}
                />
                <span>Active</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              disabled={saving}
            >
              {saving ? 'Saving...' : category ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCategoryModal;
